import { Box, Paper, Stack, Typography } from '@mui/material';
import { isNotEmptyArray, omit, withDefault, withNewOptionalValue } from '@vy/utils';
import React from 'react';
import { withSuspenseBoundary } from '../../../../../components/utility/SuspenseBoundary';
import { usePageFiltersPersistence } from '../../../../../hooks/use-page-filters-persistence';
import { useTablePagination } from '../../../../../hooks/use-table-pagination';
import { ContractsTable } from '../ContractsTable';
import { PlansHeaderSummarize } from '../PlansHeaderSummarize';
import { type GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { useTableContracts } from './use-table-contracts';
import { useContractsFilters } from '../../plans-page-hooks/use-contracts-filters';
import { usePaginationResetHandler } from '../../../../../hooks/use-pagination-reset-handler';
import { TABLE_NAMES } from '@vy/types';
import { useActiveExtractionTasks } from '../../plans-page-hooks/use-active-extraction-tasks';
import { buildPendingExtractionRow } from '../../plans-page-utils/pending-extraction-contract-row';
import { useInvalidateOnTaskCompletion } from '../../plans-page-hooks/use-invalidate-on-task-completion';
import { CONTRACTS_QUERY_SEARCH_PARAM_KEY } from '../../plans-page-hooks/use-query-input-param-consts';

const CONTRACTS_PERSISTED_FILTER_PARAMS = ['contractFilters', CONTRACTS_QUERY_SEARCH_PARAM_KEY] as const;

const ContractsTabContent: React.FC = () => {
  const gridApiRef = useGridApiRef();
  const filters = useContractsFilters();

  const { page, pageSize, onPageChange, onPageSizeChange } = useTablePagination();
  const { data: contractsResponse, isFetching } = useTableContracts(page, pageSize);
  const data = withNewOptionalValue(contractsResponse, (response) => omit(response, 'contracts'));
  const contracts = withDefault(contractsResponse?.contracts, []);

  usePaginationResetHandler(withDefault(contractsResponse?.countedFilteredContracts, 0));

  const { activeExtractionTasks } = useActiveExtractionTasks();
  const pendingExtractionRows = activeExtractionTasks.map(buildPendingExtractionRow);
  const allItems = [...pendingExtractionRows, ...contracts];

  const serverContractCount = withDefault(contractsResponse?.countedFilteredContracts, 0);
  const hasOnlyPendingRows = serverContractCount === 0 && isNotEmptyArray(activeExtractionTasks);
  const itemsCount = hasOnlyPendingRows ? pendingExtractionRows.length : serverContractCount;

  useInvalidateOnTaskCompletion(activeExtractionTasks);

  usePageFiltersPersistence(CONTRACTS_PERSISTED_FILTER_PARAMS);

  return (
    <div className="flex h-full flex-col">
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          flexWrap: "wrap",
          gap: 3,
          marginBottom: "14px"
        }}>
        <Typography
          sx={{
            fontSize: 24,
            fontStyle: "normal"
          }}>
          Plans & Pricing
        </Typography>
        <Box sx={{
          flexGrow: 1
        }}>
          <PlansHeaderSummarize data={data} />
        </Box>
      </Stack>
      <Paper
        elevation={1}
        sx={{
          height: '100%',
          borderRadius: '12px',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ContractsTable
          tableName={TABLE_NAMES.CONTRACTS}
          apiRef={gridApiRef as React.MutableRefObject<GridApiPro>}
          filters={filters}
          loadingItems={isFetching}
          items={allItems}
          itemsCount={itemsCount}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </Paper>
    </div>
  );
};

const ContractsTabContentWithSuspenseBoundary = withSuspenseBoundary(ContractsTabContent);
export { ContractsTabContentWithSuspenseBoundary as ContractsTabContent };
